import { RNG } from "rot-js";
import { shuffle } from "../../../lib/shuffle";
import { Actor, ActorBreed } from "../../actors/Actor";
import { BreedTypes } from "../../../data/breeds";
import EntityManager from "../../EntityManager";
import { ItemTypes } from "../../items/itemsTypes";
import { BasicMap } from "../BasicMap";
import { getRandomInBounds } from "./placement";
import { DMap } from "../../../lib/dMap/dMap";
import { getRampKey } from "../../actors/Ramp";
import { placeInOpenness } from "./openness";
import { gameData } from "../../GameManager";

const _OTHER = "_other" as const;
type Encounter = {
  mandatory?: Array<keyof typeof BreedTypes>;
  spawns: Partial<{ [key in keyof typeof BreedTypes & typeof _OTHER]: number }>;
};

const MIN_MONSTERS = 7;
const MAX_MONSTERS = 9;
const encounterTiers: Record<number, Encounter[]> = {
  1: [{ spawns: { whelp: 40, rat: 10, sprite: 5 } }],
  2: [
    {
      spawns: { whelp: 20, rat: 60, [_OTHER]: 10 },
    },
    {
      spawns: { sprite: 50, rat: 50, [_OTHER]: 10 },
    },
    {
      mandatory: ["ogre"],
      spawns: { sprite: 50, rat: 50, [_OTHER]: 10 },
    },
  ],
  4: [
    {
      mandatory: ["ogre"],
      spawns: { sprite: 50, ogre: 20, [_OTHER]: 10 },
    },
    {
      spawns: { vixen: 40, onion: 20, [_OTHER]: 10 },
    },
    {
      spawns: { ape: 20, wizard: 20, rat: 20, [_OTHER]: 10 },
    },
    {
      spawns: { ape: 20, poppy: 20, rat: 20, [_OTHER]: 10 },
    },
    {
      spawns: { aa: 10, vixen: 20, onion: 20, [_OTHER]: 10 },
    },
  ],
  8: [
    { spawns: { onion: 40 } },
    { mandatory: ["dragon"], spawns: { onion: 10, ogre: 20, [_OTHER]: 10 } },
    {
      mandatory: ["philospher"],
      spawns: { poppy: 20, vixen: 10, rat: 10, [_OTHER]: 10 },
    },
    {
      mandatory: [
        "aa",
        "aa",
        "aa",
        "aa",
        "aa",
        "aa",
        "aa",
        "aa",
        "aa",
        "aa",
        "aa",
        "aa",
        "aa",
        "aa",
      ],
      spawns: [],
    },
  ],
  11: [{ mandatory: ["mote"], spawns: {} }],
};

type ItemKey = keyof typeof ItemTypes;

export const placeExistingWithWord = (
  rng: typeof RNG,
  map: BasicMap,
  openness: number[],
  actor: Actor,
  word?: string
) => {
  for (let fail = 0; fail < 200; fail++) {
    const pos = getRandomInBounds(rng, {
      x: 0,
      y: 0,
      w: map.width,
      h: map.height,
    });
    const length = word?.length || actor.breed.initialPieces?.length || 1;
    if (length <= openness[pos.x + pos.y * 1000]) {
      placeInOpenness(openness, length, pos, map.height, map.width);
      actor.x = pos.x;
      actor.y = pos.y;

      gameData.actors.add(actor.id, actor);
      EntityManager.addActorPieces(actor, { x: 1, y: 0 }, word);
      return;
    }
  }
};

export const placeActor = (
  rng: typeof RNG,
  map: BasicMap,
  openness: number[],
  breed: ActorBreed,
  dMap?: DMap
) => {
  for (let fail = 0; fail < 200; fail++) {
    const pos = getRandomInBounds(rng, {
      x: 0,
      y: 0,
      w: map.width,
      h: map.height,
    });
    const length = breed.initialPieces?.length || 1;
    if (!dMap || length + 1 < dMap?.get(pos)) {
      if (length <= openness[pos.x + pos.y * 1000]) {
        placeInOpenness(openness, length, pos, map.height, map.width);
        return EntityManager.createActor(breed, pos.x, pos.y);
      }
    }
  }
};

export const placeMonsters = (
  rng: typeof RNG,
  map: BasicMap,
  openness: number[],
  dMap: DMap
) => {
  const difficulty = getRampKey(encounterTiers, map.depth);
  const encounterTable = encounterTiers[difficulty];

  if (!encounterTable) {
    console.error("did not receive encounter!");
    return;
  }

  const encounter = rng.getItem(encounterTable);
  const otherOptionSet = encounterTable.reduce((options, table) => {
    if (table === encounter) {
      return options;
    }
    if (table.mandatory) {
      options.push(...table.mandatory);
    }
    options.push(
      ...(Object.keys(table.spawns).filter(
        (s) => s !== _OTHER
      ) as (keyof typeof BreedTypes)[])
    );
    return options;
  }, [] as (keyof typeof BreedTypes)[]);

  if (encounter != null) {
    let total = rng.getUniformInt(
      MIN_MONSTERS,
      MAX_MONSTERS + (map.depth - difficulty)
    );
    encounter.mandatory?.forEach((m) => {
      placeActor(rng, map, openness, BreedTypes[m], dMap);
      total -= 1;
    });
    if (!total || Object.keys(encounter.spawns).length == 0) {
      return;
    }

    for (let i = 0; i < total; i++) {
      const toSpawn = rng.getWeightedValue(
        encounter.spawns
      ) as keyof typeof BreedTypes & typeof _OTHER;
      if (toSpawn === _OTHER) {
        const otherType = rng.getItem(otherOptionSet);
        if (otherType) {
          placeActor(rng, map, openness, BreedTypes[otherType], dMap);
        }
      } else if (toSpawn) {
        placeActor(rng, map, openness, BreedTypes[toSpawn], dMap);
      } else {
        console.error("returned no toSpawn");
      }
    }
  } else {
    console.error("no encounter found");
  }
};
